import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Clock,
  Zap,
  Sun,
  Cpu,
  Settings,
  MapPin,
  Brain,
  Database,
  BookOpen,
  CheckSquare,
  Sigma,
  Layers,
  History as HistoryIcon,
  Activity,
  Timer,
  BarChart3,
  LineChart,
  GitBranch,
  Thermometer,
  // Box, — removed with Digital Twin sidebar entry
  MonitorDot,
  Bell,
  TrendingUp,
  FileText,
  BarChart,
  ClipboardList,
  Link2,
  Shield,
  HeartPulse,
  Lightbulb,
  Wrench,
  Search,
  ClipboardCheck,
  Presentation,
  Radio,
  HardDrive,
  MessageCircle,
  ShieldCheck,
  Gauge,
  Upload,
  Server,
  Droplets,
  Fan,
} from 'lucide-react';
import { useUIStore } from '../stores/uiStore';
import { useTranslation } from '../i18n/config';

interface NavItem {
  path: string;
  icon: React.ReactNode;
  labelKey: string;
  horizonKey?: string;
}

interface NavSection {
  titleKey: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    titleKey: 'nav.inicio',
    items: [
      { path: '/', icon: <LayoutDashboard className="w-4 h-4" />, labelKey: 'nav.dashboard' },
      { path: '/sites', icon: <MapPin className="w-4 h-4" />, labelKey: 'nav.sites' },
      { path: '/shift', icon: <Clock className="w-4 h-4" />, labelKey: 'nav.shift' },
      { path: '/quick', icon: <Zap className="w-4 h-4" />, labelKey: 'nav.quickView' },
    ],
  },
  {
    titleKey: 'nav.configuracion',
    items: [
      { path: '/types', icon: <Sun className="w-4 h-4" />, labelKey: 'nav.industries' },
      { path: '/components', icon: <Cpu className="w-4 h-4" />, labelKey: 'nav.materials' },
      { path: '/presets', icon: <Settings className="w-4 h-4" />, labelKey: 'nav.presets' },
      { path: '/data-ingestion', icon: <Upload className="w-4 h-4" />, labelKey: 'nav.dataIngestion' },
      { path: '/inverter-inventory', icon: <Server className="w-4 h-4" />, labelKey: 'nav.inverterInventory' },
    ],
  },
  {
    titleKey: 'nav.entrenamiento',
    items: [
      { path: '/data-quality', icon: <ShieldCheck className="w-4 h-4" />, labelKey: 'nav.dataQuality' },
      { path: '/training', icon: <Brain className="w-4 h-4" />, labelKey: 'nav.training' },
      { path: '/pinn', icon: <Zap className="w-4 h-4" />, labelKey: 'nav.pinn' },
      { path: '/data-sources', icon: <Database className="w-4 h-4" />, labelKey: 'nav.dataSources' },
      { path: '/component-features', icon: <Layers className="w-4 h-4" />, labelKey: 'nav.componentFeatures' },
      { path: '/data-architecture', icon: <HardDrive className="w-4 h-4" />, labelKey: 'nav.dataArchitecture' },
      { path: '/model-registry', icon: <BookOpen className="w-4 h-4" />, labelKey: 'nav.modelRegistry' },
      { path: '/validation', icon: <CheckSquare className="w-4 h-4" />, labelKey: 'nav.validation' },
      { path: '/physics', icon: <Sigma className="w-4 h-4" />, labelKey: 'nav.physics' },
      { path: '/model-history', icon: <HistoryIcon className="w-4 h-4" />, labelKey: 'nav.modelHistory' },
    ],
  },
  {
    titleKey: 'nav.prediccion',
    items: [
      // 4 prediction speeds (redesign — each its own page)
      { path: '/prediction', icon: <Layers className="w-4 h-4" />, labelKey: 'nav.predictionHub' },
      { path: '/prediction/short', icon: <Bell className="w-4 h-4" />, labelKey: 'nav.earlyWarning', horizonKey: 'nav.hDaysWeeks' },
      { path: '/prediction/dc', icon: <Activity className="w-4 h-4" />, labelKey: 'nav.dcHealth', horizonKey: 'nav.hWeeks' },
      { path: '/prediction/peck', icon: <Droplets className="w-4 h-4" />, labelKey: 'nav.peckEnv', horizonKey: 'nav.hWeeksMonths' },
      { path: '/prediction/rul', icon: <Clock className="w-4 h-4" />, labelKey: 'nav.rulTier1', horizonKey: 'nav.hMonthsYears' },
      { path: '/prediction/fan', icon: <Fan className="w-4 h-4" />, labelKey: 'nav.fanHealth', horizonKey: 'nav.hComponent' },
      { path: '/prediction/thermal', icon: <Thermometer className="w-4 h-4" />, labelKey: 'nav.thermalHealthV', horizonKey: 'nav.hComponent' },
      // analytical tools
      { path: '/indicators', icon: <Gauge className="w-4 h-4" />, labelKey: 'nav.indicators' },
      { path: '/predict', icon: <Activity className="w-4 h-4" />, labelKey: 'nav.prediction' },
      { path: '/temporal', icon: <Timer className="w-4 h-4" />, labelKey: 'nav.temporal' },
      { path: '/batch', icon: <BarChart3 className="w-4 h-4" />, labelKey: 'nav.batch' },
      { path: '/analysis', icon: <LineChart className="w-4 h-4" />, labelKey: 'nav.analysis' },
      { path: '/scenarios', icon: <GitBranch className="w-4 h-4" />, labelKey: 'nav.scenarios' },
      { path: '/thermal', icon: <Thermometer className="w-4 h-4" />, labelKey: 'nav.thermalAnalysis' },
      // Digital Twin temporarily removed from sidebar — fetch timeout issue without backend
      // TODO: re-add when backend is running or demo fallback is fixed
      // { path: '/digital-twin', icon: <Box className="w-4 h-4" />, labelKey: 'nav.digitalTwin' },
    ],
  },
  {
    titleKey: 'nav.monitorizacion',
    items: [
      { path: '/fleet-health', icon: <HeartPulse className="w-4 h-4" />, labelKey: 'nav.fleetHealth' },
      { path: '/monitoring', icon: <MonitorDot className="w-4 h-4" />, labelKey: 'nav.monitoring' },
      { path: '/alerts', icon: <Bell className="w-4 h-4" />, labelKey: 'nav.alerts' },
      { path: '/waveform-analysis', icon: <Radio className="w-4 h-4" />, labelKey: 'nav.waveformAnalysis' },
      { path: '/rca-simulator', icon: <Gauge className="w-4 h-4" />, labelKey: 'nav.rcaSimulator' },
      { path: '/trends', icon: <TrendingUp className="w-4 h-4" />, labelKey: 'nav.trends' },
      { path: '/logs', icon: <FileText className="w-4 h-4" />, labelKey: 'nav.logs' },
    ],
  },
  {
    titleKey: 'nav.auditoria',
    items: [
      { path: '/history', icon: <ClipboardList className="w-4 h-4" />, labelKey: 'nav.history' },
      { path: '/reports', icon: <BarChart className="w-4 h-4" />, labelKey: 'nav.reports' },
      { path: '/statistics', icon: <Search className="w-4 h-4" />, labelKey: 'nav.statistics' },
      { path: '/traceability', icon: <Link2 className="w-4 h-4" />, labelKey: 'nav.traceability' },
      { path: '/compliance', icon: <Shield className="w-4 h-4" />, labelKey: 'nav.compliance' },
    ],
  },
  {
    titleKey: 'nav.asistente',
    items: [
      { path: '/chat-history', icon: <MessageCircle className="w-4 h-4" />, labelKey: 'nav.chatHistory' },
    ],
  },
  {
    titleKey: 'nav.admin',
    items: [
      { path: '/admin/health', icon: <HeartPulse className="w-4 h-4" />, labelKey: 'nav.adminHealth' },
      { path: '/use-cases', icon: <Lightbulb className="w-4 h-4" />, labelKey: 'nav.useCaseHub' },
      { path: '/maintenance-planner', icon: <Wrench className="w-4 h-4" />, labelKey: 'nav.maintenancePlanner' },
      { path: '/project-status', icon: <ClipboardCheck className="w-4 h-4" />, labelKey: 'nav.projectStatus' },
      { path: '/demo-guide', icon: <Presentation className="w-4 h-4" />, labelKey: 'nav.demoGuide' },
    ],
  },
];

export function Sidebar() {
  const { sidebarOpen } = useUIStore();
  const { t } = useTranslation();

  return (
    <aside
      className={`${
        sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'
      } bg-dark-900 border-r border-dark-700 h-full transition-all duration-300 flex-shrink-0`}
    >
      <div className="h-full overflow-y-auto py-4 px-3 scrollbar-thin">
        {navSections.map((section) => (
          <div key={section.titleKey} className="mb-4">
            <div className="text-xs font-semibold text-dark-500 uppercase tracking-wider px-3 mb-2">
              {t(section.titleKey)}
            </div>
            <nav className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-primary-600/20 text-primary-400 font-medium'
                        : 'text-dark-300 hover:text-white hover:bg-dark-800'
                    }`
                  }
                >
                  {item.icon}
                  <span className="flex flex-col leading-tight">
                    <span>{t(item.labelKey)}</span>
                    {item.horizonKey && (
                      <span className="text-[10px] text-dark-500 font-normal">{t(item.horizonKey)}</span>
                    )}
                  </span>
                </NavLink>
              ))}
            </nav>
          </div>
        ))}
      </div>
    </aside>
  );
}
